import { useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { copy } from "shared/assets";

import { addTemporaryToast } from "shared/components/Toast";
import {
  IAPIState,
  IBaseInfoBlock,
  IContact,
  IFile,
  IImageItem,
  IInfoBlock,
  IInfoBlockAPIOrigin,
  IInfoBlockItem,
  IInfoBlockItemType,
  IPlace,
  IStatus,
} from "shared/types";
import { IUserPermissions } from "shared/utils";

export interface EditInfoBlockItemsHookProps {
  infoBlockLabel: string;
  defaultValues?: IBaseInfoBlock;
  handleSubmit: (data: Partial<IInfoBlock>) => void;
  infoBlockApiStatus?: IAPIState;
  apiOrigin: IInfoBlockAPIOrigin;
  setApiOrigin: (origin: IInfoBlockAPIOrigin) => void;
  clearInfoBlockMeta: () => void;
  userPermissions: IUserPermissions;
}

export const useEditInfoBlock = ({
  defaultValues,
  handleSubmit,
  infoBlockApiStatus,
  apiOrigin,
  setApiOrigin,
  clearInfoBlockMeta,
}: EditInfoBlockItemsHookProps) => {
  const dispatch = useDispatch();

  const { contacts, places, files, images } = useMemo(
    () =>
      defaultValues || {
        contacts: undefined,
        places: undefined,
        files: undefined,
        images: undefined,
      },
    [defaultValues]
  );

  const patchInfoBlock = (item: IInfoBlockItemType, type: IInfoBlockItem) => {
    let patchContacts = contacts || [];
    let patchPlaces = places || [];
    let patchFiles = files || [];
    let patchImages = images || [];

    switch (type) {
      case IInfoBlockItem.contact: {
        patchContacts = [...patchContacts, item as IContact];
        break;
      }
      case IInfoBlockItem.place: {
        patchPlaces = [...patchPlaces, item as IPlace];
        break;
      }
      case IInfoBlockItem.file: {
        patchFiles = [...patchFiles, item as IFile];
        break;
      }
      case IInfoBlockItem.image: {
        patchImages = [...patchImages, item as IImageItem];
        break;
      }
    }

    handleSubmit({
      contacts: patchContacts as IContact[],
      places: patchPlaces as IPlace[],
      files: patchFiles as IFile[],
      images: patchImages as IImageItem[],
    });
  };

  const unlinkFromInfoBlock = useCallback(
    (item: IInfoBlockItemType, type: IInfoBlockItem, setOrigin?: boolean) => {
      // no unlink for files and images
      let patchContacts = contacts || [];
      let patchPlaces = places || [];

      switch (type) {
        case IInfoBlockItem.contact: {
          patchContacts = patchContacts.filter(
            (contact) => contact.id !== item.id
          );
          break;
        }
        case IInfoBlockItem.place: {
          patchPlaces = patchPlaces.filter((place) => place.id !== item.id);
          break;
        }
      }
      setOrigin && setApiOrigin(IInfoBlockAPIOrigin.Unlink);

      handleSubmit({
        contacts: patchContacts as IContact[],
        places: patchPlaces as IPlace[],
      });
    },
    [handleSubmit, contacts, places, setApiOrigin]
  );

  useEffect(() => {
    clearInfoBlockMeta();
  }, [clearInfoBlockMeta]);

  useEffect(() => {
    if (apiOrigin !== IInfoBlockAPIOrigin.Unlink) return;

    // Show unlink success as toast
    if (infoBlockApiStatus === IAPIState.succeeded) {
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: copy.toast.changed,
        })
      );
    }

    // Show unlink errors as toast
    if (infoBlockApiStatus === IAPIState.failed) {
      dispatch(
        addTemporaryToast({
          status: IStatus.error,
          message: copy.errors.general,
        })
      );
      setApiOrigin(IInfoBlockAPIOrigin.Form);
    }

    // clear meta
    clearInfoBlockMeta();
  }, [
    dispatch,
    apiOrigin,
    setApiOrigin,
    infoBlockApiStatus,
    clearInfoBlockMeta,
  ]);

  return {
    patchInfoBlock,
    unlinkFromInfoBlock,
  };
};
